/**
 * Persistence for games, teams, clubs and practice data.
 *
 * Every read and write here goes through the local IndexedDB mirror (see
 * ../offline/db.ts), never straight to the API — that is what lets a facility with
 * no connectivity for days keep recording exactly as if it were online. A write
 * lands in the mirror immediately, is queued in the outbox, and is nudged toward the
 * server opportunistically; a read simply serves whatever the mirror currently
 * holds, which ../offline/sync.ts keeps as fresh as the last successful connection
 * allowed. Callers see no difference from the old direct-to-API version beyond
 * already having to await everything.
 */

import { Game, PracticeSession } from '../types/curling';
import { CurlingClub } from '../data/curlingClubs';
import { db, StoredPracticeSession } from '../offline/db';
import { requestSync } from '../offline/sync';
import * as outbox from '../offline/outbox';

/**
 * One roster slot. `linkedUserEmail` is write-only: set it to link (or re-link) this
 * slot to a registered user, resolved server-side — leave it unset to keep whatever
 * `linkedUserId` already carries (round-tripped from a previous read), or clear both
 * to remove the link. `linkedUserId`/`linkedUserDisplayName` are otherwise read-only.
 * The link is identity only ("whose games are these"), never an access grant.
 */
export interface SavedTeamPlayer {
  displayName: string;
  linkedUserEmail?: string;
  linkedUserId?: string | null;
  linkedUserDisplayName?: string | null;
}

export interface SavedTeam {
  id: string;
  name: string;
  players: SavedTeamPlayer[];
  createdAt: number;
  /** Whether another program can find this team by name when setting up a game. */
  discoverable: boolean;
  /**
   * Soft-deleted. A program team's "delete" archives it instead of removing it — its
   * game history and any cross-program attribution persist either way, so there's
   * nothing to gain from actually deleting the row. Excluded from game-setup
   * autocomplete and cross-program search.
   */
  archived: boolean;
  /**
   * Which account this roster belongs to — read-only, populated by the server;
   * undefined for a team just built locally and not yet saved. Lets the caller tag a
   * game with which of your own teams was selected, the same way a cross-program
   * search result already carries its own `accountId`.
   */
  accountId?: string;
}

// ------------------------------------------------------------------- games

/** Upsert. Written to the mirror immediately; the server sees it on the next sync. */
export const saveGame = async (game: Game, accountId?: string): Promise<Game> => {
  await (await db()).put('games', game);
  await outbox.enqueue('games', game.id, 'upsert', accountId);
  requestSync();
  return game;
};

/** Newest first, matching the API's own ordering. */
export const getGames = async (): Promise<Game[]> => {
  const games = await (await db()).getAll('games');
  return games.sort((a, b) => b.date - a.date);
};

/** Bulk upsert, for callers that mutate several games at once. */
export const saveGames = async (games: Game[], accountId?: string): Promise<Game[]> => {
  const database = await db();
  const tx = database.transaction('games', 'readwrite');
  await Promise.all(games.map(game => tx.store.put(game)));
  await tx.done;
  await Promise.all(games.map(game => outbox.enqueue('games', game.id, 'upsert', accountId)));
  requestSync();
  return games;
};

/** Resolves to undefined for an unknown id, as the old array lookup did. */
export const getGame = async (id: string): Promise<Game | undefined> =>
  (await db()).get('games', id);

export const deleteGame = async (id: string): Promise<void> => {
  await (await db()).delete('games', id);
  await outbox.enqueue('games', id, 'delete');
  requestSync();
};

export const getGamesForSheet = async (club: string, sheet: string): Promise<Game[]> => {
  const games = await getGames();
  return games.filter(game => game.curlingClub === club && game.sheetNumber === sheet);
};

// ------------------------------------------------------------------- teams

export const saveTeam = async (team: SavedTeam, accountId?: string): Promise<SavedTeam> => {
  await (await db()).put('savedTeams', team);
  await outbox.enqueue('savedTeams', team.id, 'upsert', accountId);
  requestSync();
  return team;
};

export const getTeams = async (): Promise<SavedTeam[]> => (await db()).getAll('savedTeams');

export const deleteTeam = async (id: string): Promise<void> => {
  await (await db()).delete('savedTeams', id);
  await outbox.enqueue('savedTeams', id, 'delete');
  requestSync();
};

// ------------------------------------------------------------------- clubs

export const saveClub = async (club: CurlingClub, accountId?: string): Promise<CurlingClub> => {
  await (await db()).put('clubs', club);
  // Only ever called with a club the caller just assigned an id to (see LiveGame),
  // never one of the static reference-list entries, which carry no id at all.
  await outbox.enqueue('clubs', club.id!, 'upsert', accountId);
  requestSync();
  return club;
};

export const getClubs = async (): Promise<CurlingClub[]> => (await db()).getAll('clubs');

export const deleteClub = async (id: string): Promise<void> => {
  await (await db()).delete('clubs', id);
  await outbox.enqueue('clubs', id, 'delete');
  requestSync();
};

// ---------------------------------------------------------------- practice

/**
 * Warm-up data for one game. Replaces the practiceShots_/lsdValues_/lsdHammerTeam_
 * localStorage keys with a single resource; an absent session reads as empty, as the
 * missing localStorage key used to.
 */
export const getPracticeSession = async (gameId: string): Promise<PracticeSession> => {
  const stored = await (await db()).get('practiceSessions', gameId);
  return stored ?? { gameId, shots: [], lsdValuesTeam1: [], lsdValuesTeam2: [] };
};

export const savePracticeSession = async (
  gameId: string,
  session: PracticeSession,
): Promise<PracticeSession> => {
  const stored: StoredPracticeSession = { ...session, gameId };
  await (await db()).put('practiceSessions', stored);
  await outbox.enqueue('practiceSessions', gameId, 'upsert');
  requestSync();
  return session;
};
